import React, { useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { MatchSchema } from "../../../Schema/Game/MatchSchema";
import { Button, CustomSelect, DatePickerComponent, Error, Label } from "../..";
import { postAxios } from "../../../Services/commonService";
import { API_MATCH } from "../../../../utils/api/ApiConstant";
import { useFilteredData } from "../../../Hooks";
import DateUtils from "../../../../utils/dateUtils";
import { lowerFirstLetterUsingRegex } from "../../../helper/common";
import {
  sanitizeNameInput,
  sanitizePositiveNumberInput,
} from "../../../../utils/inputUtils";

const MatchModal = ({ handleModalHide, rowData, fetchData }) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      id: "",
      eventTypeId: null,
      competitionId: null,
      name: "",
      startDate: null,
      hour: "",
      min: "",
      sec: "",
      displayOrder: "",
    },
    resolver: yupResolver(MatchSchema),
  });

  const selectedSport = watch("eventTypeId");
  const selectedTournament = watch("competitionId");

  const { sportOptions, tournamentOptions } = useFilteredData({
    sport: selectedSport?.value,
    tournament: selectedTournament?.value,
    dependantMatch: false,
  });

  const onSubmit = async (values) => {
    console.log("Form values:", values);
    const openDate = DateUtils.toUTC(
      new Date(
        new Date(values.startDate).setHours(
          parseInt(values.hour),
          parseInt(values.min),
          parseInt(values.sec),
          0,
        ),
      ),
    );

    const body = {
      id: values?.id,
      name: values?.name || "",
      openOn: openDate,
      competitionId: values.competitionId?.value,
      eventTypeId: values.eventTypeId?.value,
      displayOrder: values.displayOrder,
    };
    console.log("API payload:", body);
    const res = await postAxios(API_MATCH, body);
    if (res?.success) {
      fetchData();
      handleModalHide();
    }
    res?.errors?.forEach((element) => {
      const field = lowerFirstLetterUsingRegex(element?.field);
      if (field)
        setError(field, {
          type: "manual",
          message: element.message,
        });
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <fieldset disabled={isSubmitting}>
        <div className="row">
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label htmlFor="id" className="form-label" isRequired={true}>
                Id
              </Label>
              <input
                type="text"
                className={`form-control ${errors.id ? "is-invalid" : ""}`}
                id="id"
                placeholder="Enter Id"
                autoFocus={true}
                tabIndex={1}
                {...register("id", {
                  onChange: (e) => {
                    e.target.value = sanitizePositiveNumberInput(
                      e.target.value,
                    );
                  },
                })}
              />
              {errors.id && <Error>{errors.id.message}</Error>}
            </div>
          </div>
          {/* Sports */}
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="eventTypeId"
                className="form-label"
                isRequired={true}
              >
                Sports
              </Label>
              <Controller
                name="eventTypeId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    options={sportOptions}
                    value={field.value}
                    onChange={(selected) => {
                      field.onChange(selected);
                      setValue("competitionId", null);
                    }}
                    onBlur={field.onBlur}
                    placeholder="Select Sports"
                    isMulti={false}
                    tabIndex={2}
                  />
                )}
              />
              {errors.eventTypeId && (
                <Error>{errors.eventTypeId.message}</Error>
              )}
            </div>
          </div>

          {/* Tournament */}
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="competitionId"
                className="form-label"
                isRequired={true}
              >
                Tournament
              </Label>
              <Controller
                name="competitionId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    options={tournamentOptions}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Select Tournament"
                    isMulti={false}
                    tabIndex={3}
                  />
                )}
              />
              {errors.competitionId && (
                <Error>{errors.competitionId.message}</Error>
              )}
            </div>
          </div>

          {/* Match Name */}
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label htmlFor="name" className="form-label" isRequired={true}>
                Match Name
              </Label>
              <input
                type="text"
                className="form-control"
                id="name"
                placeholder="Enter name"
                {...register("name", {
                  onChange: (e) => {
                    e.target.value = sanitizeNameInput(e.target.value);
                  },
                })}
                tabIndex={4}
              />
              {errors.name && <Error>{errors.name.message}</Error>}
            </div>
          </div>
          <div className="col-md-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="displayOrder"
                className="form-label"
                isRequired={true}
              >
                Display Order
              </Label>
              <input
                type="text"
                className={`form-control ${errors.displayOrder ? "is-invalid" : ""}`}
                id="displayOrder"
                placeholder="Enter Display Order"
                {...register("displayOrder", {
                  onChange: (e) => {
                    e.target.value = sanitizePositiveNumberInput(
                      e.target.value,
                    );
                  },
                })}
                tabIndex={5}
              />
              {errors.displayOrder && (
                <Error>{errors.displayOrder.message}</Error>
              )}
            </div>
          </div>
          {/* Start Date */}
          <div className="col-lg-6 col-12 ">
            <div className="form-group">
              <Label
                htmlFor="startDate"
                className="form-label"
                isRequired={true}
              >
                Start Date
              </Label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePickerComponent
                    selectedDate={field.value}
                    onChange={field.onChange}
                    placeholderText="Pick a start date"
                    minDate={new Date()}
                    tabIndex={6}
                  />
                )}
              />
              {errors.startDate && <Error>{errors.startDate.message}</Error>}
            </div>
          </div>
          {/* Time */}
          <div className="col-lg-6 col-12 ">
            <Label htmlFor="time" className="form-label" isRequired={true}>
              Time
            </Label>
            <div className="time-input-group d-flex gap-1 align-items-center ">
              <input
                type="text"
                className="form-control"
                id="hour"
                placeholder="HH"
                min="0"
                max="23"
                {...register("hour", {
                  onChange: (e) => {
                    e.target.value = sanitizePositiveNumberInput(
                      e.target.value,
                    );
                  },
                })}
                tabIndex={7}
              />
              <span>:</span>
              <input
                type="text"
                className="form-control"
                id="min"
                placeholder="MM"
                min="0"
                max="59"
                {...register("min", {
                  onChange: (e) => {
                    e.target.value = sanitizePositiveNumberInput(
                      e.target.value,
                    );
                  },
                })}
                tabIndex={8}
              />
              <span>:</span>
              <input
                type="text"
                className="form-control"
                id="sec"
                placeholder="SS"
                min="0"
                max="59"
                {...register("sec", {
                  onChange: (e) => {
                    e.target.value = sanitizePositiveNumberInput(
                      e.target.value,
                    );
                  },
                })}
                tabIndex={9}
              />
            </div>
            {(errors.hour || errors.min || errors.sec) && (
              <Error>Invalid time input</Error>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-end w-100 mt-2">
          <Button
            type="button"
            className="btn btn_main btn_muted px-3 py-2 mx-1"
            onClick={handleModalHide}
            tabIndex={11}
          >
            Close
          </Button>
          <Button
            type="submit"
            className="btn btn_main px-3 py-2 mx-1"
            tabIndex={10}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? "Loading.." : "Submit"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};

export default MatchModal;
